import types from '../../mutation-types';
import ConversationApi from '../../../api/inbox/conversation';
import MessageApi from '../../../api/inbox/message';
import { MESSAGE_STATUS, MESSAGE_TYPE } from 'shared/constants/messages';
import { createPendingMessage } from 'dashboard/helper/commons';
import {
  buildConversationList,
  isOnMentionsView,
  isOnUnattendedView,
  isOnFoldersView,
} from './helpers/actionHelpers';
import messageReadActions from './actions/messageReadActions';
import messageTranslateActions from './actions/messageTranslateActions';
import { emitter } from 'shared/helpers/mitt';
import * as Sentry from '@sentry/vue';
import {
  handleVoiceCallCreated,
  handleVoiceCallUpdated,
} from 'dashboard/helper/voice';

export const hasMessageFailedWithExternalError = pendingMessage => {
  // This helper is used to check if the message has failed with an external error.
  // We have two cases
  // 1. Messages that fail from the UI itself (due to large attachments or a failed network):
  //    In this case, the message will have a status of failed but no external error. So we need to create that message again
  // 2. Messages sent from Chatwoot but failed to deliver to the customer for some reason (user blocking or client system down):
  //    In this case, the message will have a status of failed and an external error. So we need to retry that message
  const { content_attributes: contentAttributes, status } = pendingMessage;
  const externalError = contentAttributes?.external_error ?? '';
  return status === MESSAGE_STATUS.FAILED && externalError !== '';
};

// actions
const actions = {
  getConversation: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.show(conversationId);
      commit(types.UPDATE_CONVERSATION, response.data);
      commit(`contacts/${types.SET_CONTACT_ITEM}`, response.data.meta.sender);
    } catch (error) {
      // Ignore error
    }
  },

  fetchAllConversations: async ({ commit, state, dispatch }) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const params = state.conversationFilters;
      const {
        data: { data },
      } = await ConversationApi.get(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        params.assigneeType
      );
    } catch (error) {
      // Handle error
    }
  },

  fetchConversationSection: async (
    { commit, dispatch, rootGetters, state },
    { filters, filterKey, quiet = false, probe = false }
  ) => {
    const epoch = rootGetters['conversationPage/getFetchEpoch'];
    try {
      const {
        data: { data },
      } = await ConversationApi.get(filters);
      // A view-context reset (company/label/status switch) happened while this
      // request was in flight — its rows belong to the previous context.
      if (rootGetters['conversationPage/getFetchEpoch'] !== epoch) return null;
      // Probe: membership ids only — no store writes. Used by the quiet
      // sync's deep pages so a background walk can't insert dozens of
      // not-yet-paginated rows above the viewport.
      if (probe) return data;
      if (quiet) {
        // Background resync: merge rows only. Pagination cursors, stats meta
        // and loading flags stay untouched so the visible list never moves —
        // autonomous events update rows and badges, never the page. Skip rows
        // the store already has in a NEWER version (a websocket row update
        // can land while this response is in flight; last-write-wins would
        // resurrect the older state).
        const knownUpdatedAt = new Map(
          state.allConversations.map(c => [c.id, Number(c.updated_at) || 0])
        );
        const freshRows = (data.payload || []).filter(row => {
          const existing = knownUpdatedAt.get(row.id);
          return (
            existing === undefined || (Number(row.updated_at) || 0) >= existing
          );
        });
        commit(types.SET_ALL_CONVERSATION, freshRows);
        dispatch('conversationLabels/setBulkConversationLabels', freshRows);
        return data;
      }
      buildConversationList({ commit, dispatch }, filters, data, filterKey);
      return data;
    } catch (error) {
      return null;
    }
  },

  fetchFilteredConversations: async ({ commit, dispatch }, params) => {
    commit(types.SET_LIST_LOADING_STATUS);
    try {
      const { data } = await ConversationApi.filter(params);
      buildConversationList(
        { commit, dispatch },
        params,
        data,
        'appliedFilters'
      );
    } catch (error) {
      // Handle error
    }
  },

  emptyAllConversations({ commit }) {
    commit(types.EMPTY_ALL_CONVERSATION);
  },

  clearSelectedState({ commit }) {
    commit(types.CLEAR_CURRENT_CHAT_WINDOW);
  },

  fetchPreviousMessages: async ({ commit }, data) => {
    try {
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages(data);
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: data.conversationId,
        data: meta,
      });
      commit(types.SET_PREVIOUS_CONVERSATIONS, {
        id: data.conversationId,
        data: payload,
      });
      // An empty page only means "top of history" for a plain `before` fetch —
      // a ranged (`after`+`before`) gap fetch can be legitimately empty and
      // must not disable further scroll-up loading.
      if (!payload.length && !data.after) {
        commit(types.SET_ALL_MESSAGES_LOADED, data.conversationId);
      }
    } catch (error) {
      // Handle error
    }
  },

  fetchNewerMessages: async ({ commit, state }, { conversationId, after }) => {
    try {
      const {
        data: { payload },
      } = await MessageApi.getPreviousMessages({ conversationId, after });
      const chat = state.allConversations.find(c => c.id === conversationId);
      if (!chat || !payload.length) return;
      const knownIds = new Set(chat.messages.map(m => m.id));
      const fresh = payload.filter(m => !knownIds.has(m.id));
      if (!fresh.length) return;
      const merged = [...chat.messages, ...fresh].sort(
        (a, b) => new Date(a.created_at) - new Date(b.created_at)
      );
      commit(types.SET_MISSING_MESSAGES, { id: conversationId, data: merged });
    } catch (error) {
      // Handle error
    }
  },

  fetchAllAttachments: async ({ commit }, conversationId) => {
    let attachments = [];

    try {
      const { data } = await ConversationApi.getAllAttachments(conversationId);
      attachments = data.payload;
    } catch (error) {
      // in case of error, log the error and continue
      Sentry.setContext('Conversation', {
        id: conversationId,
      });
      Sentry.captureException(error);
    } finally {
      // we run the commit even if the request fails
      // this ensures that the `attachment` variable is always present on chat
      commit(types.SET_ALL_ATTACHMENTS, {
        id: conversationId,
        data: attachments,
      });
    }
  },

  syncActiveConversationMessages: async (
    { commit, state, dispatch },
    { conversationId }
  ) => {
    const { allConversations, syncConversationsMessages } = state;
    const lastMessageId = syncConversationsMessages[conversationId];
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    try {
      const { messages } = selectedChat;
      // Fetch all the messages after the last message id
      const {
        data: { meta, payload },
      } = await MessageApi.getPreviousMessages({
        conversationId,
        after: lastMessageId,
      });
      commit(`conversationMetadata/${types.SET_CONVERSATION_METADATA}`, {
        id: conversationId,
        data: meta,
      });
      // Find the messages that are not already present in the store
      const missingMessages = payload.filter(
        message => !messages.find(item => item.id === message.id)
      );
      selectedChat.messages.push(...missingMessages);
      // Sort the messages by created_at
      const sortedMessages = selectedChat.messages.sort((a, b) => {
        return new Date(a.created_at) - new Date(b.created_at);
      });
      commit(types.SET_MISSING_MESSAGES, {
        id: conversationId,
        data: sortedMessages,
      });
      commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
        conversationId,
        messageId: null,
      });
      dispatch('markMessagesRead', { id: conversationId }, { root: true });
    } catch (error) {
      // Handle error
    }
  },

  setConversationLastMessageId: async (
    { commit, state },
    { conversationId }
  ) => {
    const { allConversations } = state;
    const selectedChat = allConversations.find(
      conversation => conversation.id === conversationId
    );
    if (!selectedChat) return;
    const { messages } = selectedChat;
    const lastMessage = messages.last();
    if (!lastMessage) return;
    commit(types.SET_LAST_MESSAGE_ID_IN_SYNC_CONVERSATION, {
      conversationId,
      messageId: lastMessage.id,
    });
  },

  async setActiveChat({ commit, dispatch }, { data, after }) {
    commit(types.SET_CURRENT_CHAT_WINDOW, data);
    commit(types.CLEAR_ALL_MESSAGES_LOADED, data.id);
    if (data.dataFetched === undefined) {
      try {
        const before = data.messages.length ? data.messages[0].id : undefined;
        // A deep-link target at/after the newest loaded message (e.g. a
        // trailing private note whose id outran the last reply) would invert
        // the ranged window (after >= before → messages_between returns
        // nothing): fetch plain history instead, then pull the tail so the
        // target is in the store for SCROLL_TO_MESSAGE.
        const gapAfter =
          after && before && Number(after) < Number(before) ? after : undefined;
        await dispatch('fetchPreviousMessages', {
          after: gapAfter,
          before,
          conversationId: data.id,
        });
        if (after && !gapAfter && before) {
          await dispatch('fetchNewerMessages', {
            conversationId: data.id,
            after: before,
          });
        }
        commit(types.SET_CHAT_DATA_FETCHED, data.id);
      } catch (error) {
        // Ignore error
      }
    } else if (after) {
      // Deep link (?messageId=) into a conversation whose recent window was
      // already fetched: the target may fall outside the loaded window on
      // either side, and the upstream branch above only runs on first open.
      // Pull the gap (messages_between is target-inclusive) or the tail so the
      // follow-up SCROLL_TO_MESSAGE finds its element instead of falling to
      // the bottom.
      const messages = data.messages || [];
      const targetLoaded = messages.some(m => Number(m.id) === Number(after));
      if (!targetLoaded && messages.length) {
        if (Number(messages[0].id) > Number(after)) {
          try {
            await dispatch('fetchPreviousMessages', {
              after,
              before: messages[0].id,
              conversationId: data.id,
            });
          } catch (error) {
            // Ignore error
          }
        } else if (Number(messages[messages.length - 1].id) < Number(after)) {
          await dispatch('fetchNewerMessages', {
            conversationId: data.id,
            after: messages[messages.length - 1].id,
          });
        }
      }
    }
  },

  assignAgent: async ({ dispatch }, { conversationId, agentId }) => {
    try {
      const response = await ConversationApi.assignAgent({
        conversationId,
        agentId,
      });
      dispatch('setCurrentChatAssignee', {
        conversationId,
        assignee: response.data,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatAssignee({ commit }, { conversationId, assignee }) {
    commit(types.ASSIGN_AGENT, { conversationId, assignee });
  },

  assignTeam: async ({ dispatch }, { conversationId, teamId }) => {
    try {
      const response = await ConversationApi.assignTeam({
        conversationId,
        teamId,
      });
      dispatch('setCurrentChatTeam', { team: response.data, conversationId });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatTeam({ commit }, { team, conversationId }) {
    commit(types.ASSIGN_TEAM, { team, conversationId });
  },

  toggleStatus: async (
    { commit },
    { conversationId, status, snoozedUntil = null, customAttributes = null }
  ) => {
    try {
      // Update custom attributes first if provided
      if (customAttributes) {
        await ConversationApi.updateCustomAttributes({
          conversationId,
          customAttributes,
        });
        commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
          conversationId,
          customAttributes,
        });
      }

      const {
        data: {
          payload: {
            current_status: updatedStatus,
            snoozed_until: updatedSnoozedUntil,
          } = {},
        } = {},
      } = await ConversationApi.toggleStatus({
        conversationId,
        status,
        snoozedUntil,
      });
      commit(types.CHANGE_CONVERSATION_STATUS, {
        conversationId,
        status: updatedStatus,
        snoozedUntil: updatedSnoozedUntil,
      });
    } catch (error) {
      // Handle error
    }
  },

  createPendingMessageAndSend: async ({ dispatch }, data) => {
    const pendingMessage = createPendingMessage(data);
    dispatch('sendMessageWithData', pendingMessage);
  },

  sendMessageWithData: async ({ commit }, pendingMessage) => {
    const { conversation_id: conversationId, id } = pendingMessage;
    try {
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        status: MESSAGE_STATUS.PROGRESS,
      });
      const response = hasMessageFailedWithExternalError(pendingMessage)
        ? await MessageApi.retry(conversationId, id)
        : await MessageApi.create(pendingMessage);
      commit(types.ADD_MESSAGE, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, {
        ...response.data,
        status: MESSAGE_STATUS.SENT,
      });
    } catch (error) {
      const errorMessage = error.response
        ? error.response.data.error
        : undefined;
      commit(types.ADD_MESSAGE, {
        ...pendingMessage,
        meta: {
          error: errorMessage,
        },
        status: MESSAGE_STATUS.FAILED,
      });
      throw error;
    }
  },

  addMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    if (message.message_type === MESSAGE_TYPE.INCOMING) {
      commit(types.SET_CONVERSATION_CAN_REPLY, {
        conversationId: message.conversation_id,
        canReply: true,
      });
      commit(types.ADD_CONVERSATION_ATTACHMENTS, message);
    }
    handleVoiceCallCreated(message, rootGetters?.getCurrentUserID);
  },

  updateMessage({ commit, rootGetters }, message) {
    commit(types.ADD_MESSAGE, message);
    handleVoiceCallUpdated(commit, message, rootGetters?.getCurrentUserID);
  },

  deleteMessage: async function deleteLabels(
    { commit },
    { conversationId, messageId }
  ) {
    try {
      const { data } = await MessageApi.delete(conversationId, messageId);
      commit(types.ADD_MESSAGE, data);
      commit(types.DELETE_CONVERSATION_ATTACHMENTS, data);
    } catch (error) {
      throw new Error(error);
    }
  },

  deleteConversation: async ({ commit, dispatch, state }, conversationId) => {
    try {
      await ConversationApi.delete(conversationId);
      commit(types.DELETE_CONVERSATION, conversationId);
      // Refetch badge counts with the live list filters — a bare {} falls back to
      // the backend's DEFAULT_STATUS='open' while the list now defaults to 'all'.
      dispatch('conversationStats/get', state.conversationFilters, {
        root: true,
      });
    } catch (error) {
      throw new Error(error);
    }
  },

  addConversation({ commit, state, dispatch, rootState }, conversation) {
    const { currentInbox, appliedFilters } = state;
    const {
      inbox_id: inboxId,
      meta: { sender },
    } = conversation;
    const hasAppliedFilters = !!appliedFilters.length;
    const isMatchingInboxFilter =
      !currentInbox || Number(currentInbox) === inboxId;
    if (
      !hasAppliedFilters &&
      !isOnFoldersView(rootState) &&
      !isOnMentionsView(rootState) &&
      !isOnUnattendedView(rootState) &&
      isMatchingInboxFilter
    ) {
      commit(types.ADD_CONVERSATION, conversation);
      dispatch('contacts/setContact', sender);
    }
  },

  addMentions({ dispatch, rootState }, conversation) {
    if (isOnMentionsView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  addUnattended({ dispatch, rootState }, conversation) {
    if (isOnUnattendedView(rootState)) {
      dispatch('updateConversation', conversation);
    }
  },

  updateConversation({ commit, dispatch }, conversation) {
    const {
      meta: { sender },
    } = conversation;
    commit(types.UPDATE_CONVERSATION, conversation);

    dispatch('conversationLabels/setConversationLabel', {
      id: conversation.id,
      data: conversation.labels,
    });

    dispatch('contacts/setContact', sender);
  },

  updateConversationLastActivity(
    { commit },
    { conversationId, lastActivityAt }
  ) {
    commit(types.UPDATE_CONVERSATION_LAST_ACTIVITY, {
      lastActivityAt,
      conversationId,
    });
  },

  setChatStatusFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_STATUS_FILTER, data);
  },

  setChatSortFilter({ commit }, data) {
    commit(types.CHANGE_CHAT_SORT_FILTER, data);
  },

  updateAssignee({ commit }, data) {
    commit(types.UPDATE_ASSIGNEE, data);
  },

  updateConversationContact({ commit }, data) {
    if (data.id) {
      commit(`contacts/${types.SET_CONTACT_ITEM}`, data);
    }
    commit(types.UPDATE_CONVERSATION_CONTACT, data);
  },

  setActiveInbox({ commit }, inboxId) {
    commit(types.SET_ACTIVE_INBOX, inboxId);
  },

  muteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.mute(conversationId);
      commit(types.MUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  unmuteConversation: async ({ commit }, conversationId) => {
    try {
      await ConversationApi.unmute(conversationId);
      commit(types.UNMUTE_CONVERSATION);
    } catch (error) {
      //
    }
  },

  sendEmailTranscript: async (_, { conversationId, email }) => {
    await ConversationApi.sendEmailTranscript({ conversationId, email });
  },

  updateCustomAttributes: async (
    { commit },
    { conversationId, customAttributes }
  ) => {
    try {
      const response = await ConversationApi.updateCustomAttributes({
        conversationId,
        customAttributes,
      });
      const { custom_attributes } = response.data;
      commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
        conversationId,
        customAttributes: custom_attributes,
      });
    } catch (error) {
      // Handle error
    }
  },

  // MakerHub: "Resolve" → mark handled. Server stamps mh_handled_at + clears the
  // explicit overrides (pin_pending, priority) via merge-safe jsonb_set without
  // touching status; we commit the returned custom_attributes + priority so the
  // Urgent/Pending getters drop it locally, then refetch the tab badge counts
  // (server-computed meta) so the numbers update on the spot — covers both call
  // sites (ResolveAction header button + ConversationCard context menu).
  markConversationHandled: async ({ commit }, conversationId) => {
    const response = await ConversationApi.markHandled(conversationId);
    const {
      custom_attributes,
      priority = null,
      mh_needs_reply: needsReply,
      mh_effective_recency_at: effectiveRecencyAt,
      mh_effective_recency_id: effectiveRecencyId,
      mh_unreplied: unreplied,
      mh_last_visible_at: lastVisibleAt,
      mh_last_visible_id: lastVisibleId,
    } = response.data;
    commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
      conversationId,
      customAttributes: custom_attributes,
    });
    commit(types.ASSIGN_PRIORITY, { conversationId, priority });
    commit(types.UPDATE_CONVERSATION_REPLY_STATE, {
      conversationId,
      needsReply,
      effectiveRecencyAt,
      effectiveRecencyId,
      unreplied,
      lastVisibleAt,
      lastVisibleId,
    });
    emitter.emit('fetch_conversation_stats');
    emitter.emit('fetch_needs_reply_count');
  },

  // MakerHub: manual "Set to Urgent" — the human counterpart of the AI patrol's
  // queue placement. mark_handled first clears EVERY current red-tab membership
  // (derived urgent/pending via mh_handled_at, plus the pin_pending/priority
  // overrides), then priority=urgent pins it to the Urgent tab (finder id-unions
  // it in) — so the conversation lands in exactly one red tab from any prior state.
  markConversationUrgent: async ({ commit }, conversationId) => {
    const response = await ConversationApi.markHandled(conversationId);
    await ConversationApi.togglePriority({
      conversationId,
      priority: 'urgent',
    });
    commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
      conversationId,
      customAttributes: response.data.custom_attributes,
    });
    commit(types.ASSIGN_PRIORITY, { conversationId, priority: 'urgent' });
    emitter.emit('fetch_conversation_stats');
  },

  // MakerHub: manual "Set to Pending" — mark_handled clears the current red-tab
  // membership, then pin_pending=true pins it to the Pending tab. Commits the
  // SECOND response's custom_attributes (mh_handled_at merged with pin_pending).
  markConversationPending: async ({ commit }, conversationId) => {
    await ConversationApi.markHandled(conversationId);
    const response = await ConversationApi.togglePending({
      conversationId,
      pending: true,
    });
    commit(types.UPDATE_CONVERSATION_CUSTOM_ATTRIBUTES, {
      conversationId,
      customAttributes: response.data.custom_attributes,
    });
    commit(types.ASSIGN_PRIORITY, { conversationId, priority: null });
    emitter.emit('fetch_conversation_stats');
  },

  setConversationFilters({ commit }, data) {
    commit(types.SET_CONVERSATION_FILTERS, data);
  },

  clearConversationFilters({ commit }) {
    commit(types.CLEAR_CONVERSATION_FILTERS);
  },

  setChatListFilters({ commit }, data) {
    commit(types.SET_CHAT_LIST_FILTERS, data);
  },

  updateChatListFilters({ commit }, data) {
    commit(types.UPDATE_CHAT_LIST_FILTERS, data);
  },

  assignPriority: async ({ dispatch }, { conversationId, priority }) => {
    try {
      await ConversationApi.togglePriority({
        conversationId,
        priority,
      });

      dispatch('setCurrentChatPriority', {
        priority,
        conversationId,
      });
    } catch (error) {
      // Handle error
    }
  },

  setCurrentChatPriority({ commit }, { priority, conversationId }) {
    commit(types.ASSIGN_PRIORITY, { priority, conversationId });
  },

  setContextMenuChatId({ commit }, chatId) {
    commit(types.SET_CONTEXT_MENU_CHAT_ID, chatId);
  },

  getInboxCaptainAssistantById: async ({ commit }, conversationId) => {
    try {
      const response = await ConversationApi.getInboxAssistant(conversationId);
      commit(types.SET_INBOX_CAPTAIN_ASSISTANT, response.data);
    } catch (error) {
      // Handle error
    }
  },

  ...messageReadActions,
  ...messageTranslateActions,
};

export default actions;
